<template>
  <v-row class="filter-bar flex-grow-0 ma-0 mb-3" align="center" dense>
    <v-col cols="8" class="py-2 px-0">
      <div class="d-flex assortment-filter">
        <span class="assortment-text mx-2 text-no-wrap">{{ title }}</span>
        <DocsLink v-if="docPath" :link="`toolguide/${docPath}.html`" />
        <div v-if="!archived" class="d-flex filter-box">
          <span class="mx-2 text-no-wrap">{{ $t('actions.sortBy') }}:</span>
          <v-select
            v-model="sortOrder"
            :items="sortOptions"
            item-value="key"
            hide-details
            item-title="value"
            :list-props="{
              density: 'compact',
              lines: false,
              class: 'text-caption',
              itemClass: 'text-caption',
            }"
            :menu-props="{
              origin: 'overlap',
              contentClass: 'text-caption',
            }"
            :placeholder="tkey('selectedFilterOption')"
            bg-color="white"
            style="width: 210px"
            flat
            @update:model-value="onSort"
          />
        </div>
      </div>
    </v-col>

    <v-col cols="4" class="d-flex justify-end">
      <div class="d-flex assortment-filter align-center justify-end">
        <v-btn class="clear px-4" :disabled="disableClear" primary @click="clearSearch"> x </v-btn>

        <RtlsSearch
          v-model="searchText"
          :placeholder="placeholder"
          append-inner-icon="mdi-magnify"
          density="compact"
          hide-details
          clear-on-selection
          flat
          @update:model-value="onSearchDebounced"
        />
      </div>
    </v-col>
  </v-row>
</template>

<script>
import { debounce, capitalize } from 'lodash';
import SortOptions from '@enums/workpackage-sort-options';
import { useInternationalization } from '@/composables/use-internationalization';

export default {
  props: {
    archived: {
      type: Boolean,
      required: false,
      default: () => false,
    },

    docPath: {
      type: String,
      required: false,
      default: '',
    },

    entity: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: {
    'on-search': null,
    'on-sort': null,
  },

  setup() {
    const { tkey } = useInternationalization('workpackages.toolbar');
    return { tkey };
  },

  data() {
    const sortKeys = [
      SortOptions.byNameAsc,
      SortOptions.byNameDesc,
      SortOptions.byCreatedDateAsc,
      SortOptions.byCreatedDateDesc,
      SortOptions.byLastModifiedDateAsc,
      SortOptions.byLastModifiedDateDesc,
    ];
    const sortOptions = sortKeys.map(option => {
      return { key: option, value: this.tkey(`sortOptions.${option}`) };
    });
    return {
      sortOptions,
      searchText: '',
      sortOrder: SortOptions.byCreatedDateDesc,
      onSearchDebounced: debounce(
        () => {
          this.$emit('on-search', this.searchText);
        },
        200,
        { maxWait: 500 }
      ),
    };
  },

  computed: {
    title() {
      const localisationKey = this.archived ? 'archived' : 'active';
      return capitalize(
        this.$t(`workpackages.toolbar.${localisationKey}`, {
          entity: this.$t(`entities.${this.entity}`),
        })
      );
    },

    placeholder() {
      const entityTranslation = this.$t(`entities.${this.entity}`);
      return `${entityTranslation} ${this.tkey('name')}`;
    },

    disableClear() {
      return this.searchText.length === 0;
    },
  },

  mounted() {
    this.onSort();
  },

  methods: {
    onSort() {
      this.$emit('on-sort', this.sortOrder);
    },

    clearSearch() {
      this.searchText = '';
      this.$emit('on-search', this.searchText);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.filter-bar {
  margin-top: 15px;
  border-top: 1px solid $assortment-primary-colour;
  background-color: $assortment-filter-bar-bg-colour;

  .assortment-text {
    font-size: 1.4rem;
    font-weight: 600;
    color: $assortment-text-colour;
  }

  .assortment-filter {
    width: 100%;
    align-items: baseline;
  }

  .date-filter {
    width: 160px;
  }

  .filter-box {
    align-items: center;
    border-left: #6e8b9c 1px solid;
    border-right: #6e8b9c 1px solid;
    padding: 0 20px;
    margin: 0 20px;

    :deep(.v-field) {
      height: 100%;
      align-items: center;
    }

    :deep(.v-field__input) {
      line-height: 1.5em;
      padding: 0 0 0 10px;
    }
  }

  .clear {
    width: 24px !important;
    min-width: 24px;
    border-radius: 0 !important;
    font-weight: bold;
    max-height: 27px;
    height: 27px !important;
  }
}
</style>
